import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { Button, alpha, useTheme, buttonClasses, svgIconClasses, CircularProgress, useMediaQuery } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import useCurrencyFormat from '../hooks/useCurrencyFormat';
import { getStateLayerColor, StateLayer } from '../theme/color.utils';
import BasketSummaryDrawer from './BasketSummaryDrawer';
import { basketDataAtom, isBasketDrawerOpenAtom, isBasketLoadingAtom, isRemoteBasketEmptyAtom } from './state/state';

export default function BasketButton(): React.JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isRemoteBasketEmpty = useAtomValue(isRemoteBasketEmptyAtom);
  const basketData = useAtomValue(basketDataAtom);
  const isBasketLoading = useAtomValue(isBasketLoadingAtom);
  const setIsBasketDrawerOpen = useSetAtom(isBasketDrawerOpenAtom);
  const currencyFormat = useCurrencyFormat();

  const handleButtonClick = (): void => {
    setIsBasketDrawerOpen(true);
  };

  return (
    <>
      <Button
        onClick={handleButtonClick}
        startIcon={isBasketLoading ? <CircularProgress size="24px" sx={{ color: 'onSurface.main' }} /> : <ShoppingCartOutlinedIcon />}
        variant="text"
        className={isRemoteBasketEmpty ? 'empty' : ''}
        sx={{
          gap: 1,
          height: 48,
          maxHeight: 48,
          minWidth: 48,
          fontSize: '1rem',
          // add !important to override all spacing rules on button since it's a specific use case here
          px: {
            xs: '0 !important',
            lg: '12px !important',
          },
          color: 'onSurface.main',
          backgroundColor: 'tertiaryContainer.main',
          '&:hover': {
            backgroundColor: getStateLayerColor(StateLayer.Layer8, theme.palette.tertiaryContainer.main, theme.palette.onTertiaryContainer.main),
          },
          '&:focus': {
            backgroundColor: getStateLayerColor(StateLayer.Layer12, theme.palette.tertiaryContainer.main, theme.palette.onTertiaryContainer.main),
          },
          '&:focus:not(:focus-visible)': {
            backgroundColor: 'tertiaryContainer.main',
          },
          '&:active': {
            backgroundColor: getStateLayerColor(StateLayer.Layer16, theme.palette.tertiaryContainer.main, theme.palette.onTertiaryContainer.main),
          },
          '&.empty': {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: alpha(theme.palette.onSurface.main, StateLayer.Layer8),
            },
            '&:focus': {
              backgroundColor: alpha(theme.palette.onSurface.main, StateLayer.Layer12),
            },
            '&:focus:not(:focus-visible)': {
              backgroundColor: 'transparent',
            },
            '&:active': {
              backgroundColor: alpha(theme.palette.onSurface.main, StateLayer.Layer16),
            },
          },
          [`& .${buttonClasses.startIcon}`]: {
            m: 0,
            [`& > .${svgIconClasses.root}`]: {
              height: '24px',
              width: '24px',
            },
          },
        }}
      >
        {!isMobile && currencyFormat.format(basketData?.total_price_excl_tax || 0)}
      </Button>
      {basketData && <BasketSummaryDrawer />}
    </>
  );
}
